/**
 * Admin panel for managing public Landing Pages. Lives inside the
 * Marketing hub at `/admin/campaigns?tab=landing_pages`, alongside
 * the Campaigns tab.
 *
 * Landing Pages differ from Campaigns:
 *   - No bundle whitelist. A landing page is a generic entry point that
 *     drops the visitor into the ordering flow for a whole product
 *     family (with an optional pre-selected bundle OR product SKU).
 *   - Persistent (no start/end date). Marketing sites keep the link.
 *   - Carries default language + default currency so the marketing
 *     site can hand off locale without an extra query param.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import {
    Plus, Loader2, Trash2, Pencil, Link as LinkIcon, Rocket, Copy, ExternalLink, Sun, Moon,
} from 'lucide-react';

import { API_BASE } from '../lib/apiBase';
const API = `${API_BASE}/api`;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// A landing page can start from any of these three wizard modes.
const STARTING_STEPS = [
    { value: 'quick_order',   label: 'Quick Order (bundle grid)' },
    { value: 'guided_setup',  label: 'Guided Setup (step-by-step)' },
    { value: 'expert_setup',  label: 'Expert Setup (single-page form)' },
];

// Presentation/UX version of the anonymous flow. Only meaningful when
// `starting_step === 'guided_setup'` — the redesigned V2 is a Guided
// Setup replacement. Quick Order + Expert Setup stay on V1.
const FLOW_VERSIONS = [
    { value: 'v1', label: 'Guided Setup / V1 (current)' },
    { value: 'v2', label: 'Guided Setup / V2 (new)' },
];

const PRODUCT_FAMILIES = [
    { value: 'vinyl',   label: 'Vinyl' },
    { value: 'cd',      label: 'CD' },
    { value: 'dvd',     label: 'DVD' },
    { value: 'bluray',  label: 'Blu-ray' },
];

// Kept in sync with `SUPPORTED_CURRENCIES` on the backend.
const SUPPORTED_CURRENCIES = ['EUR', 'DKK', 'SEK', 'NOK', 'USD', 'GBP'];
const SUPPORTED_LANGUAGES = [
    { value: 'en', label: 'English' },
    { value: 'da', label: 'Danish' },
    { value: 'sv', label: 'Swedish' },
    { value: 'no', label: 'Norwegian' },
    { value: 'de', label: 'German' },
    { value: 'fr', label: 'French' },
];

const EMPTY = {
    id: null,
    title: '',
    description: '',
    slug: '',
    product_family: 'vinyl',
    default_bundle_id: '',
    default_product_sku: '',
    starting_step: 'guided_setup',
    flow_version: 'v1',
    default_language: 'en',
    default_currency: 'EUR',
    is_active: true,
    show_prices: true,
};

const slugify = (s) =>
    (s || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60);

export default function LandingPagesPanel() {
    const [pages, setPages] = useState([]);
    const [bundles, setBundles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [draft, setDraft] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const slugTouchedRef = useRef(false);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [pRes, ...bResults] = await Promise.all([
                axios.get(`${API}/admin/landing-pages`, { headers: authHeader() }),
                axios.get(`${API}/product-config/admin/bundles?product_family=vinyl`, { headers: authHeader() }),
                axios.get(`${API}/product-config/admin/bundles?product_family=cd`, { headers: authHeader() }),
                axios.get(`${API}/product-config/admin/bundles?product_family=dvd`, { headers: authHeader() }),
                axios.get(`${API}/product-config/admin/bundles?product_family=bluray`, { headers: authHeader() }),
            ]);
            setPages(pRes.data || []);
            const merged = bResults.flatMap((r) => r.data || []);
            const seen = new Set();
            const unique = [];
            for (const b of merged) {
                if (!seen.has(b.id)) { seen.add(b.id); unique.push(b); }
            }
            setBundles(unique);
        } catch {
            toast.error('Failed to load landing pages');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const openCreate = () => {
        setDraft(EMPTY);
        slugTouchedRef.current = false;
        setModalOpen(true);
    };

    const openEdit = (page) => {
        setDraft({
            ...EMPTY,
            ...page,
            default_bundle_id: page.default_bundle_id || '',
            default_product_sku: page.default_product_sku || '',
        });
        slugTouchedRef.current = true;
        setModalOpen(true);
    };

    const updateField = (patch) => setDraft((d) => ({ ...d, ...patch }));

    // Bundles filtered by the currently-picked product_family. If the
    // admin switches family we blank any stale default_bundle_id.
    const familyBundles = useMemo(
        () => bundles.filter((b) => b.product_family === draft.product_family),
        [bundles, draft.product_family],
    );

    const save = async () => {
        if (!draft.title.trim()) { toast.error('Title is required'); return; }
        if (!draft.slug.trim()) { toast.error('URL slug is required'); return; }
        setSaving(true);
        try {
            const payload = {
                title: draft.title.trim(),
                description: draft.description?.trim() || null,
                slug: draft.slug.trim().toLowerCase(),
                product_family: draft.product_family,
                default_bundle_id: draft.default_bundle_id || null,
                default_product_sku: draft.default_product_sku?.trim() || null,
                starting_step: draft.starting_step,
                flow_version: draft.starting_step === 'guided_setup' ? (draft.flow_version || 'v1') : 'v1',
                default_language: draft.default_language,
                default_currency: draft.default_currency,
                is_active: draft.is_active,
                show_prices: draft.show_prices !== false,
            };
            if (draft.id) {
                await axios.put(`${API}/admin/landing-pages/${draft.id}`, payload, { headers: authHeader() });
                toast.success('Landing page updated');
            } else {
                await axios.post(`${API}/admin/landing-pages`, payload, { headers: authHeader() });
                toast.success('Landing page created');
            }
            setModalOpen(false);
            fetchAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to save landing page');
        } finally {
            setSaving(false);
        }
    };

    const del = async (page) => {
        if (!window.confirm(`Delete landing page "${page.title}"?`)) return;
        try {
            await axios.delete(`${API}/admin/landing-pages/${page.id}`, { headers: authHeader() });
            toast.success('Landing page deleted');
            fetchAll();
        } catch {
            toast.error('Failed to delete landing page');
        }
    };

    const copyLink = (slug, theme) => {
        const base = `${window.location.origin}/l/${slug}`;
        const url = theme ? `${base}?theme=${theme}` : base;
        navigator.clipboard.writeText(url).then(() =>
            toast.success(theme ? `Link copied (${theme} preview)` : 'Link copied'),
        );
    };

    return (
        <Card data-testid="landing-pages-panel">
            <CardHeader>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Rocket className="h-5 w-5 text-emerald-600" />
                            Landing Pages
                        </CardTitle>
                        <CardDescription>
                            Permanent marketing entry points. Deep-link the marketing site
                            straight into the ordering flow for any product family — no login
                            required until the visitor is ready to save their draft.
                        </CardDescription>
                    </div>
                    <Button onClick={openCreate} data-testid="new-landing-page-btn">
                        <Plus className="h-4 w-4 mr-1" /> New Landing Page
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="py-10 flex justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    </div>
                ) : pages.length === 0 ? (
                    <div className="text-center py-10 text-sm text-slate-500">
                        No landing pages yet. Create one to embed a "Calculate your price" button on the marketing site.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {pages.map((p) => (
                            <div
                                key={p.id}
                                className="border rounded-lg p-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors"
                                data-testid={`landing-page-row-${p.slug}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-slate-900 truncate">{p.title}</span>
                                        {!p.is_active && <Badge variant="outline" className="text-slate-500">Inactive</Badge>}
                                        <Badge variant="secondary" className="text-[10px]">
                                            {PRODUCT_FAMILIES.find((f) => f.value === p.product_family)?.label || p.product_family}
                                        </Badge>
                                        <Badge variant="secondary" className="text-[10px]">
                                            {STARTING_STEPS.find((s) => s.value === p.starting_step)?.label?.split(' (')[0] || p.starting_step}
                                        </Badge>
                                        {p.starting_step === 'guided_setup' && p.flow_version === 'v2' && (
                                            <Badge className="text-[10px] bg-amber-500 text-white border-0" data-testid={`landing-page-v2-badge-${p.slug}`}>
                                                V2
                                            </Badge>
                                        )}
                                        <Badge variant="outline" className="text-[10px] text-slate-500">
                                            {p.default_language?.toUpperCase()} · {p.default_currency}
                                        </Badge>
                                    </div>
                                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                                        <LinkIcon className="h-3 w-3" />
                                        <span className="font-mono">/l/{p.slug}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <Button
                                        size="sm" variant="ghost"
                                        onClick={() => copyLink(p.slug)}
                                        title="Copy public link"
                                        data-testid={`copy-link-${p.slug}`}
                                    >
                                        <Copy className="h-4 w-4" />
                                    </Button>
                                    <div className="flex items-center border border-border rounded-md overflow-hidden">
                                        <Button
                                            size="sm" variant="ghost"
                                            className="h-8 px-2 rounded-none"
                                            onClick={() => window.open(`/l/${p.slug}?theme=light`, '_blank')}
                                            title="Preview in light mode (opens with ?theme=light)"
                                            data-testid={`preview-light-${p.slug}`}
                                        >
                                            <Sun className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            size="sm" variant="ghost"
                                            className="h-8 px-2 rounded-none border-l border-border"
                                            onClick={() => window.open(`/l/${p.slug}?theme=dark`, '_blank')}
                                            title="Preview in dark mode (opens with ?theme=dark)"
                                            data-testid={`preview-dark-${p.slug}`}
                                        >
                                            <Moon className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <Button
                                        size="sm" variant="ghost"
                                        onClick={() => window.open(`/l/${p.slug}`, '_blank')}
                                        title="Open landing page (visitor default — no theme forced)"
                                        data-testid={`open-${p.slug}`}
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        size="sm" variant="ghost"
                                        onClick={() => openEdit(p)}
                                        data-testid={`edit-${p.slug}`}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        size="sm" variant="ghost"
                                        onClick={() => del(p)}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        data-testid={`delete-${p.slug}`}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>

            {/* Editor dialog */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{draft.id ? 'Edit landing page' : 'New landing page'}</DialogTitle>
                        <DialogDescription>
                            Permanent public entry point. Every product page on the marketing
                            site can deep-link to `/l/&lt;slug&gt;` and open the ordering flow.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div>
                            <Label>Title</Label>
                            <Input
                                value={draft.title}
                                onChange={(e) => {
                                    const t = e.target.value;
                                    updateField({
                                        title: t,
                                        // Auto-derive slug from the title until the admin edits
                                        // it manually — this mirrors CampaignsPanel behaviour.
                                        slug: slugTouchedRef.current ? draft.slug : slugify(t),
                                    });
                                }}
                                placeholder="e.g. Calculate your Vinyl price"
                                data-testid="lp-title"
                            />
                        </div>

                        <div>
                            <Label>URL slug</Label>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-slate-500 font-mono">/l/</span>
                                <Input
                                    value={draft.slug}
                                    onChange={(e) => { slugTouchedRef.current = true; updateField({ slug: e.target.value }); }}
                                    onBlur={() => updateField({ slug: slugify(draft.slug) })}
                                    placeholder="calculate-price-vinyl"
                                    className="font-mono"
                                    data-testid="lp-slug"
                                />
                            </div>
                        </div>

                        <div>
                            <Label>Description</Label>
                            <Textarea
                                value={draft.description}
                                onChange={(e) => updateField({ description: e.target.value })}
                                placeholder="Short internal description — not shown on the public page."
                                rows={2}
                                data-testid="lp-description"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Product family</Label>
                                <Select
                                    value={draft.product_family}
                                    onValueChange={(v) => updateField({ product_family: v, default_bundle_id: '' })}
                                >
                                    <SelectTrigger data-testid="lp-family"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {PRODUCT_FAMILIES.map((f) => (
                                            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Starting step</Label>
                                <Select
                                    value={draft.starting_step}
                                    onValueChange={(v) => updateField({ starting_step: v })}
                                >
                                    <SelectTrigger data-testid="lp-starting-step"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {STARTING_STEPS.map((s) => (
                                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {draft.starting_step === 'guided_setup' && (
                            <div>
                                <Label>Flow version</Label>
                                <Select
                                    value={draft.flow_version || 'v1'}
                                    onValueChange={(v) => updateField({ flow_version: v })}
                                >
                                    <SelectTrigger data-testid="lp-flow-version"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {FLOW_VERSIONS.map((v) => (
                                            <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-slate-500 mt-1">
                                    V1 is the current live experience. V2 is the redesigned single-hero flow — only Step 1 is available today; Steps 2–8 will follow.
                                </p>
                            </div>
                        )}

                        <div>
                            <Label>Default bundle (optional)</Label>
                            <Select
                                value={draft.default_bundle_id || 'none'}
                                onValueChange={(v) => updateField({ default_bundle_id: v === 'none' ? '' : v })}
                            >
                                <SelectTrigger data-testid="lp-default-bundle">
                                    <SelectValue placeholder="No bundle preselected" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— No bundle preselected —</SelectItem>
                                    {familyBundles.map((b) => (
                                        <SelectItem key={b.id} value={b.id}>{b.title}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500 mt-1">
                                If set, the wizard opens with this bundle pre-selected. Ignored when Starting step is Expert Setup.
                            </p>
                        </div>

                        <div>
                            <Label>Default product SKU (optional)</Label>
                            <Input
                                value={draft.default_product_sku}
                                onChange={(e) => updateField({ default_product_sku: e.target.value })}
                                placeholder="e.g. cd_audio or vinyl_12_black"
                                className="font-mono"
                                data-testid="lp-default-sku"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                                For Guided / Expert Setup — jumps straight to a specific base product SKU. Only used when no bundle is preselected.
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Default language</Label>
                                <Select
                                    value={draft.default_language}
                                    onValueChange={(v) => updateField({ default_language: v })}
                                >
                                    <SelectTrigger data-testid="lp-language"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {SUPPORTED_LANGUAGES.map((l) => (
                                            <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Default currency</Label>
                                <Select
                                    value={draft.default_currency}
                                    onValueChange={(v) => updateField({ default_currency: v })}
                                >
                                    <SelectTrigger data-testid="lp-currency"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {SUPPORTED_CURRENCIES.map((c) => (
                                            <SelectItem key={c} value={c}>{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 pt-2">
                            <Switch
                                checked={draft.is_active}
                                onCheckedChange={(v) => updateField({ is_active: v })}
                                id="lp-active"
                                data-testid="lp-active"
                            />
                            <Label htmlFor="lp-active" className="cursor-pointer">
                                Active — publicly accessible at /l/{draft.slug || '…'}
                            </Label>
                        </div>

                        <div className="flex items-center gap-2 pt-2">
                            <Switch
                                checked={draft.show_prices !== false}
                                onCheckedChange={(v) => updateField({ show_prices: v })}
                                id="lp-show-prices"
                                data-testid="lp-show-prices"
                            />
                            <Label htmlFor="lp-show-prices" className="cursor-pointer">
                                Show Prices — display pricing in the public configuration flow
                            </Label>
                        </div>
                        {draft.show_prices === false && (
                            <p className="text-xs text-slate-500 -mt-1 pl-11" data-testid="lp-show-prices-hint">
                                Prices are hidden throughout the public flow. Customers see prices only after signing in.
                            </p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
                        <Button onClick={save} disabled={saving} data-testid="lp-save">
                            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                            {draft.id ? 'Save changes' : 'Create landing page'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
